import { React, useState, useEffect, memo } from 'react';
import "./FilterRangeSlider.css";
import { Box, Slider } from '@mui/material';

const roundToNearestPercentage = (value, isMin) => {
    if (isMin) {
        return value >= 100 ? Math.round(value / 100) * 100 : Math.floor(value / 10) * 10;
    } else {
        if (value > 100) {
            return Math.ceil(value / 100) * 100;
        }
        return Math.ceil(value / 10) * 10;
    }
};

const FilterRangeSlider = ({ item, title, sliderValue, min = 0, max = 100, setSelectedItems, addNode, updateNode, path, uom, filterType, round_filter_info = true }) => {
    const originalMin = item?.min ?? min;
    const originalMax = item?.max ?? max;

    const itemMin = roundToNearestPercentage(originalMin, true);
    const itemMax = roundToNearestPercentage(originalMax, false);

    const [tempSliderValue, setTempSliderValue] = useState(sliderValue || [itemMin, itemMax]);
    const [appliedSliderValue, setAppliedSliderValue] = useState(sliderValue || [itemMin, itemMax]);
    const [isSliding, setIsSliding] = useState(false);
    const [isModified, setIsModified] = useState(false);

    // Track input values as strings for temporary editing
    const [minInputStr, setMinInputStr] = useState(tempSliderValue[0].toString());
    const [maxInputStr, setMaxInputStr] = useState(tempSliderValue[1].toString());

    const [applyOnNextChange, setApplyOnNextChange] = useState(false);

    // Sync string inputs with tempSliderValue changes
    useEffect(() => {
        if (sliderValue) {
            setMinInputStr(sliderValue[0].toString());
            setMaxInputStr(sliderValue[1].toString());
        } else {
            setMinInputStr(itemMin.toString());
            setMaxInputStr(itemMax.toString());
        }
    }, [sliderValue, itemMin, itemMax]);


    useEffect(() => {
    if (applyOnNextChange) {
        handleApply();
        setApplyOnNextChange(false); // نرجع نطفي الفلاغ
    }
}, [tempSliderValue]);

    useEffect(() => {
        if (!sliderValue) {
            const newValue = [itemMin, itemMax];
            setAppliedSliderValue(newValue);
            setTempSliderValue(newValue);
        } else {
            setAppliedSliderValue(sliderValue);
            setTempSliderValue(sliderValue);
        }
    }, [sliderValue, itemMin, itemMax]);

    useEffect(() => {
        const isValid = !isNaN(tempSliderValue[0]) && !isNaN(tempSliderValue[1]);
        const isDifferent = tempSliderValue[0] !== appliedSliderValue[0] || tempSliderValue[1] !== appliedSliderValue[1];
        setIsModified(isValid && isDifferent);
    }, [tempSliderValue, appliedSliderValue]);

    const handleSliderChange = (event, newValue) => {
        setIsSliding(true);
        if (filterType === "Normal Slider") {
            setTempSliderValue(newValue);
        } else {
            filterType === "Disable Left Slider" 
                ? setTempSliderValue([itemMin, newValue])
                : setTempSliderValue([newValue, itemMax]);
        }
    };

  const handleSliderCommit = () => {
    setIsSliding(false);
    const newRange = [...tempSliderValue]; // Use latest slider values directly
    applySliderValuesDirectly(newRange);
};

   const handleApply = () => {
    let finalMin = parseFloat(minInputStr);
    let finalMax = parseFloat(maxInputStr);

    // If inputs are invalid, fallback to previous values
    if (isNaN(finalMin)) finalMin = tempSliderValue[0];
    if (isNaN(finalMax)) finalMax = tempSliderValue[1];

    // Clamp to itemMin and itemMax
    finalMin = Math.max(itemMin, Math.min(finalMin, itemMax));
    finalMax = Math.max(itemMin, Math.min(finalMax, itemMax));

    // Swap if needed
    if (finalMin > finalMax) [finalMin, finalMax] = [finalMax, finalMin];

    const newRange = [finalMin, finalMax];

    setTempSliderValue(newRange);

    setSelectedItems(prev => {
        const fullPath = [...path, { key: title, value: 0 }];
        const sliderField = prev[title] || [];
        if (sliderField.length === 0) {
            addNode(fullPath, { is_group: 0, min: newRange[0], max: newRange[1] });
        } else {
            updateNode(fullPath, { is_group: 0, min: newRange[0], max: newRange[1] }, title);
        }
        return { ...prev, [title]: newRange };
    });

    setAppliedSliderValue(newRange);
};

const applySliderValuesDirectly = (newRange) => {
    setSelectedItems(prev => {
        const fullPath = [...path, { key: title, value: 0 }];
        const sliderField = prev[title] || [];
        if (sliderField.length === 0) {
            addNode(fullPath, { is_group: 0, min: newRange[0], max: newRange[1] });
        } else {
            updateNode(fullPath, { is_group: 0, min: newRange[0], max: newRange[1] }, title);
        }
        return { ...prev, [title]: newRange };
    });

    setAppliedSliderValue(newRange);
};

    const handleMinInputChange = (e) => {
        setMinInputStr(e.target.value);
    };

    const handleMaxInputChange = (e) => {
        setMaxInputStr(e.target.value);
    };

    const validateMinInput = (applyAfter = false) => {
        const numValue = parseFloat(minInputStr);
        if (isNaN(numValue)) {
            setMinInputStr(tempSliderValue[0].toString());
            return;
        }

        let newMin = Math.max(itemMin, numValue);
        newMin = Math.min(newMin, itemMax);
        let newMax = tempSliderValue[1];

        if (newMin > newMax) {
            [newMin, newMax] = [newMax, newMin];
            newMax = Math.min(newMax, itemMax);
            newMin = Math.max(newMin, itemMin);
        }

        setTempSliderValue([newMin, newMax]);
        if (applyAfter) handleApply(); // 👈 تنفيذ apply بعد التحقق
    };

    const validateMaxInput = (applyAfter = false) => {
        const numValue = parseFloat(maxInputStr);
        if (isNaN(numValue)) {
            setMaxInputStr(tempSliderValue[1].toString());
            return;
        }

        let newMax = Math.min(itemMax, numValue);
        newMax = Math.max(newMax, itemMin);
        let newMin = tempSliderValue[0];

        if (newMax < newMin) {
            [newMin, newMax] = [newMax, newMin];
            newMin = Math.max(newMin, itemMin);
            newMax = Math.min(newMax, itemMax);
        }

        setTempSliderValue([newMin, newMax]);
        if (applyAfter) handleApply(); // 👈 تنفيذ apply بعد التحقق
    };

    const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
        e.preventDefault();
        if (e.target.name === 'min') {
            validateMinInput(); // تحديث القيمة فقط
        } else if (e.target.name === 'max') {
            validateMaxInput(); // تحديث القيمة فقط
        }
        setApplyOnNextChange(true); // فعل الفلاغ ليتم التحديث بعد setTempSliderValue
    }
};

    const handleGoClick = () => {
        validateMinInput();  // validate & update tempSliderValue
        validateMaxInput();  // validate & update tempSliderValue
        setApplyOnNextChange(true); // wait for useEffect to apply updated values
    };

    const isSingleSlider = filterType !== "Normal Slider";
    const displayValue = isSliding ? tempSliderValue : appliedSliderValue;
    const sliderVal = isSingleSlider 
        ? filterType === "Disable Left Slider" 
            ? displayValue[1] 
            : displayValue[0]
        : displayValue;

    return (
        <div className='products-sidebar-accordion-container'>
            <Box sx={{ width: "80%", textAlign: "center" }}>
                <Slider
                    sx={{
                        "& .MuiSlider-valueLabel": {
                            borderRadius: "5px",
                            backgroundColor: "var(--main-color)",
                            color: "#FFF",
                            fontFamily: "Sfpro400",
                            fontSize: "10px",
                            letterSpacing: "1.2px",
                            padding: "3px 12px",
                        },
                        "& .MuiSlider-thumb": {
                            padding: "0.8px",
                            outline: "1px solid rgb(180, 180, 180)",
                            backgroundColor: "var(--main-color)",
                            border: "2px solid rgb(255, 255, 255)",
                            width: "9px",
                            height: "9px",
                        },
                    }}
                    onKeyDown={(e) => {
                        // Prevent arrow keys from changing the slider
                        if (["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(e.key)) {
                            e.preventDefault();
                        }
                    }}
                    style={{ color: "#239DAB", marginTop: '30px' }}
                    value={sliderVal}
                    onChange={handleSliderChange}
                    onChangeCommitted={handleSliderCommit}
                    valueLabelDisplay="on"
                    valueLabelFormat={(value) => `${value}${uom}`}
                    min={itemMin}
                    max={itemMax}
                    step={(item.field_type === "Float" || item.field_type === "Float Range") ? 0.1 : 1}
                />

                <div className="products-sidebar-ranges">
                    <input
                        name="min"  

                        type="number"
                        className={`products-sidebar-range-input ${filterType === "Disable Left Slider" ? 'range-input-disabled' : ''}`}
                        placeholder={`${itemMin}${uom}`}
                        value={minInputStr}
                        onChange={handleMinInputChange}
                     
                        onKeyPress={handleKeyPress}
                        disabled={filterType === "Disable Left Slider"}
                        step={(item.field_type === "Float" || item.field_type === "Float Range") ? 0.1 : 1}
                    />
                    <div> - </div>
                    <input
                    name="max"
                        type="number"
                        className={`products-sidebar-range-input ${filterType === "Disable Right Slider" ? 'range-input-disabled' : ''}`}
                        placeholder={`${itemMax}${uom}`}
                        value={maxInputStr}
                        onChange={handleMaxInputChange}
                        
                        onKeyPress={handleKeyPress}
                        disabled={filterType === "Disable Right Slider"}
                        step={(item.field_type === "Float" || item.field_type === "Float Range") ? 0.1 : 1}
                    />
                    <button 
                        onClick={handleGoClick}
                        className={`go-range-button`}
                    >
                        Go
                    </button>
                </div>
            </Box>
        </div>
    );
};

export default FilterRangeSlider;